import React from 'react'
import PropTypes from 'prop-types'
import { Modal, IconButton, useTheme, alpha } from '@mui/material'
import { Close } from '@mui/icons-material'
import AppBoxComponent from './AppBoxComponent'
import AppTypographyComponent from './AppTypographyComponent'
import AppCardComponent from './AppCardComponent'
import { typography } from 'theme'

const AppModalComponent = ({
  open = false,
  onClose = () => { },
  title = '',
  subtitle = '',
  children,
  width = 400,
  maxWidth = '90vw',
  showCloseButton = true,
  sx = {}
}) => {
  const theme = useTheme()

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...sx,
        '& .MuiBackdrop-root': {
          backgroundColor: alpha(theme.palette.common.black, 0.7),
        },
      }}
    >
      <AppCardComponent
        sx={{
          width,
          maxWidth,
          border: `1px solid ${alpha(theme.palette.common.white, 0.04)}`,
          borderRadius: theme.shape.borderRadius,
          outline: 'none',
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
          '& .MuiCard-root': {
            backgroundColor: '#13172A',
          },
          '& .MuiCardContent-root': {
            backgroundColor: '#13172A',
            padding: { xs: theme.spacing(2), sm: theme.spacing(2.5) },
            '&:last-child': {
              paddingBottom: { xs: theme.spacing(2), sm: theme.spacing(2.5) },
            },
          },
        }}
      >
        {/* Modal Header */}
        {(title || showCloseButton) && (
          <AppBoxComponent
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: subtitle ? 'flex-start' : 'center',
              mb: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
              pb: (title || subtitle) ? { xs: theme.spacing(1.5), sm: theme.spacing(2) } : 0,
              borderBottom: (title || subtitle) ? `1px solid ${alpha(theme.palette.common.white, 0.08)}` : 'none',
            }}
          >
            {(title || subtitle) ? (
              <AppBoxComponent sx={{ display: 'flex', flexDirection: 'column' }}>
                {title && (
                  <AppTypographyComponent
                    variant="h6"
                    sx={{
                      color: theme.palette.custom?.text?.heading || theme.palette.common.white,
                      fontWeight: typography.fontWeight.semibold,
                      fontSize: { xs: typography.fontSize.h6, sm: typography.fontSize.h5 },
                    }}
                  >
                    {title}
                  </AppTypographyComponent>
                )}
                {subtitle && (
                  <AppTypographyComponent
                    sx={{
                      color: alpha(theme.palette.custom?.text?.heading || theme.palette.common.white, 0.6),
                      fontSize: { xs: '11px', md: '13px' },
                      mt: 0.5,
                    }}
                  >
                    {subtitle}
                  </AppTypographyComponent>
                )}
              </AppBoxComponent>
            ) : (
              <AppBoxComponent />
            )}
            {showCloseButton && (
              <IconButton
                onClick={onClose}
                sx={{
                  color: theme.palette.custom?.text?.helperEnabled || theme.palette.grey[400],
                  p: theme.spacing(0.5),
                  mt: subtitle ? -0.5 : 0, // Adjust for subtitle height
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.common.white, 0.1),
                  },
                }}
              >
                <Close />
              </IconButton>
            )}
          </AppBoxComponent>
        )}

        {/* Modal Content */}
        <AppBoxComponent>
          {children}
        </AppBoxComponent>
      </AppCardComponent>
    </Modal>
  )
}

AppModalComponent.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showCloseButton: PropTypes.bool,
  sx: PropTypes.object
}

AppModalComponent.defaultProps = {
  open: false,
  onClose: () => { },
  title: '',
  subtitle: '',
  children: null,
  width: 400,
  maxWidth: '90vw',
  showCloseButton: true,
  sx: {}
}

export default AppModalComponent
